import { useQuery, useMutation, useQueryClient } from 'react-query';
import { promptsApi } from '../../../../../api/prompts';

export const usePromptsBlocks = ( tenant_id ) => {

	const { data, isLoading, isError, error, refetch } = useQuery(
		[ 'prompts-blocks', tenant_id ],
		() => promptsApi.getPromptsBlocks( { tenant_id } ),
		{
			staleTime : 5 * 60 * 1000, // 5 minutes
			cacheTime : 10 * 60 * 1000, // 10 minutes
		}
	);

	return {
		promptsBlocks: data?.data,
		isLoading,
		isError,
		error,
		refetch,
	};
};

export const usePrompts = () => {
	const { data, isLoading, isError, error, refetch } = useQuery(
		[ 'prompts' ],
		promptsApi.getPrompts,
		{
			staleTime : 5 * 60 * 1000, // 5 minutes
			cacheTime : 10 * 60 * 1000, // 10 minutes
		}
	);

	return {
		prompts: data?.data,
		isLoading,
		isError,
		error,
		refetch,
	};
};

export const useUpdatePrompt = () => {
	const queryClient = useQueryClient();

	const { mutate, mutateAsync, isLoading, isError, error, isSuccess } = useMutation(
		promptsApi.updatePrompt,
		{
			onSuccess: ( data, variables ) => {
				// Invalidate and refetch prompts list
				queryClient.invalidateQueries( [ 'prompts' ] );
				queryClient.invalidateQueries( [ 'prompts-blocks' ] );

				// Optionally update the specific prompt in cache
				queryClient.setQueryData( [ 'prompts' ], ( oldData ) => {
					if ( !oldData?.data ) return oldData;
					return {
						...oldData,
						data: oldData.data.map( prompt =>
							prompt.id === variables.id ? { ...prompt, ...variables } : prompt
						)
					};
				} );
			},
			onError: ( error ) => {
				console.error( 'Error updating prompt:', error );
			}
		}
	);

	return {
		updatePrompt      : mutate,
		updatePromptAsync : mutateAsync,
		isLoading,
		isError,
		error,
		isSuccess,
	};
};

export const useCreatePrompt = () => {
	const queryClient = useQueryClient();

	const { mutate, mutateAsync, isLoading, isError, error, isSuccess } = useMutation(
		promptsApi.createPrompt,
		{
			onSuccess: () => {
				// Invalidate and refetch prompts list
				queryClient.invalidateQueries( [ 'prompts' ] );
				queryClient.invalidateQueries( [ 'prompts-blocks' ] );
			},
			onError: ( error ) => {
				console.error( 'Error creating prompt:', error );
			}
		}
	);

	return {
		createPrompt      : mutate,
		createPromptAsync : mutateAsync,
		isLoading,
		isError,
		error,
		isSuccess,
	};
};

export const useDeletePrompt = () => {
	const queryClient = useQueryClient();

	const { mutate, mutateAsync, isLoading, isError, error, isSuccess } = useMutation(
		promptsApi.deletePrompt,
		{
			onSuccess: ( data, promptId ) => {
				// Invalidate and refetch prompts list
				queryClient.invalidateQueries( [ 'prompts' ] );
				queryClient.invalidateQueries( [ 'prompts-blocks' ] );

				// Remove the deleted prompt from cache
				queryClient.setQueryData( [ 'prompts' ], ( oldData ) => {
					if ( !oldData?.data ) return oldData;

					return {
						...oldData,
						data: oldData.data.filter( prompt => prompt.id !== promptId )
					};
				} );
			},
			onError: ( error ) => {
				console.error( 'Error deleting prompt:', error );
			}
		}
	);

	return {
		deletePrompt      : mutate,
		deletePromptAsync : mutateAsync,
		isLoading,
		isError,
		error,
		isSuccess,
	};
};
